import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { openPrintUrl } from '../../../../lib/openPrint';
import {
  CHART_COLORS,
  buildPrintReportUrl,
  downloadCsv,
  toIsoDate,
} from '../../reportUtils';
import { KpiCard, ChartCard, EmptyState } from './IepProgressReport';

// ---------------------------------------------------------------------------
// Compliance / IEP Reviews Report
//
// The one report a district cares about most: are we hitting our federal
// 12-month annual-review deadlines?
//
//   * Bucket every active student by how far away their next annual review
//     is: overdue, this month, next 30/60/90 days, beyond 90, and "no
//     date set" (which is its own compliance risk).
//   * Bar chart shows count per bucket.
//   * Table lists every student with a date, sorted soonest first.
//   * "Missing IEP date" table calls out students who need paperwork
//     entered so the app can start tracking their cadence.
// ---------------------------------------------------------------------------

const BUCKETS = [
  { key: 'overdue', label: 'Overdue', color: CHART_COLORS.danger, tone: 'danger' },
  { key: 'in-30', label: 'Next 30 days', color: '#F97316', tone: 'warning' },
  { key: 'in-60', label: '31–60 days', color: CHART_COLORS.warning, tone: 'warning' },
  { key: 'in-90', label: '61–90 days', color: '#84CC16', tone: 'success' },
  { key: 'beyond', label: 'Beyond 90 days', color: CHART_COLORS.success, tone: 'success' },
  { key: 'no-date', label: 'No date set', color: CHART_COLORS.slate, tone: 'warning' },
];

function bucketFor(days) {
  if (days === null) return 'no-date';
  if (days < 0) return 'overdue';
  if (days <= 30) return 'in-30';
  if (days <= 60) return 'in-60';
  if (days <= 90) return 'in-90';
  return 'beyond';
}

const ComplianceReport = ({ studentFilter, dateRange, printMode = false }) => {
  const navigate = useNavigate();
  const { students, isLive } = useStudents();
  const { goals } = useIEPGoals();
  const animate = !printMode;
  const handlePrint = () => {
    const url = buildPrintReportUrl('compliance', { dateRange, studentFilter });
    openPrintUrl(url);
  };

  const rollups = useMemo(() => {
    const activeStudents = (Array.isArray(students) ? students : []).filter(
      (s) => (s.status || 'active') !== 'archived',
    );
    const filterActive = studentFilter && studentFilter.size > 0;
    const inScope = filterActive
      ? activeStudents.filter((s) => studentFilter.has(s.id))
      : activeStudents;

    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const goalsPerStudent = new Map();
    for (const g of Array.isArray(goals) ? goals : []) {
      goalsPerStudent.set(
        g.studentId,
        (goalsPerStudent.get(g.studentId) || 0) + 1,
      );
    }

    const enriched = inScope.map((s) => {
      const dueRaw = s.iepAnnualReviewDue;
      const due = dueRaw ? new Date(`${String(dueRaw).slice(0, 10)}T12:00:00`) : null;
      const days = due
        ? Math.round((due - startOfToday) / (1000 * 60 * 60 * 24))
        : null;
      return {
        id: s.id,
        name:
          `${s.firstName || ''} ${s.lastName || ''}`.trim() ||
          'Unnamed student',
        grade: s.grade || '',
        iepStartDate: s.iepStartDate || null,
        reviewDue: dueRaw || null,
        daysUntil: days,
        bucket: bucketFor(days),
        goalCount: goalsPerStudent.get(s.id) || 0,
      };
    });

    const bucketCounts = new Map();
    for (const row of enriched) {
      bucketCounts.set(row.bucket, (bucketCounts.get(row.bucket) || 0) + 1);
    }
    const chartData = BUCKETS.map((b) => ({
      name: b.label,
      value: bucketCounts.get(b.key) || 0,
      color: b.color,
    }));

    // Compliance score: percentage of students whose next review is in the
    // future (not overdue). Missing dates are counted as compliance risk.
    const withOrOnTime = enriched.filter(
      (r) => r.bucket !== 'overdue' && r.bucket !== 'no-date',
    ).length;
    const complianceScore = enriched.length
      ? Math.round((withOrOnTime / enriched.length) * 100)
      : 100;

    const overdueRows = enriched
      .filter((r) => r.bucket === 'overdue')
      .sort((a, b) => (a.daysUntil ?? 0) - (b.daysUntil ?? 0));

    const upcomingRows = enriched
      .filter(
        (r) =>
          r.bucket === 'in-30' ||
          r.bucket === 'in-60' ||
          r.bucket === 'in-90' ||
          r.bucket === 'beyond',
      )
      .sort((a, b) => (a.daysUntil ?? 0) - (b.daysUntil ?? 0));

    const missingDateRows = enriched.filter((r) => r.bucket === 'no-date');

    return {
      totalStudents: enriched.length,
      overdueCount: overdueRows.length,
      dueSoonCount:
        (bucketCounts.get('in-30') || 0) + (bucketCounts.get('in-60') || 0),
      missingCount: missingDateRows.length,
      chartData,
      complianceScore,
      overdueRows,
      upcomingRows,
      missingDateRows,
      enriched,
      todayIso: toIsoDate(startOfToday),
    };
  }, [students, goals, studentFilter]);

  const handleExportCsv = () => {
    const filename = `iep-compliance-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsv(
      filename,
      rollups.enriched.map((r) => ({
        student: r.name,
        grade: r.grade,
        iepStartDate: r.iepStartDate || '',
        reviewDue: r.reviewDue || '',
        daysUntil: r.daysUntil === null ? '' : r.daysUntil,
        status: BUCKETS.find((b) => b.key === r.bucket)?.label || '',
        goalCount: r.goalCount,
      })),
      [
        { key: 'student', label: 'Student' },
        { key: 'grade', label: 'Grade' },
        { key: 'iepStartDate', label: 'IEP start date' },
        { key: 'reviewDue', label: 'Annual review due' },
        { key: 'daysUntil', label: 'Days until review' },
        { key: 'status', label: 'Status' },
        { key: 'goalCount', label: 'Active goals' },
      ],
    );
  };

  const openStudent = (id) => {
    navigate(`/student-profile-management/${encodeURIComponent(id)}`);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon="Shield"
          label="Compliance score"
          value={`${rollups.complianceScore}%`}
          tone={
            rollups.complianceScore >= 90
              ? 'success'
              : rollups.complianceScore >= 70
              ? 'warning'
              : 'danger'
          }
          subtitle="Reviews in the future / not missing"
        />
        <KpiCard
          icon="AlertOctagon"
          label="Overdue reviews"
          value={String(rollups.overdueCount)}
          tone={rollups.overdueCount > 0 ? 'danger' : 'success'}
          subtitle={rollups.overdueCount > 0 ? 'Take action today' : 'Nothing overdue'}
        />
        <KpiCard
          icon="CalendarClock"
          label="Due in 60 days"
          value={String(rollups.dueSoonCount)}
          tone={rollups.dueSoonCount > 0 ? 'warning' : 'success'}
          subtitle="Schedule meetings this month"
        />
        <KpiCard
          icon="HelpCircle"
          label="Missing IEP date"
          value={String(rollups.missingCount)}
          tone={rollups.missingCount > 0 ? 'warning' : 'success'}
          subtitle="Enter annual-review date on profile"
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            IEP compliance
          </h2>
          <p className="text-sm text-muted-foreground">
            Federal law requires an IEP annual review every 12 months. This
            report shows how close (or how past) each student is against
            their {isLive ? 'stored' : 'demo-mode'} review date. Update a
            review date by opening the student and editing the IEP timeline
            section.
          </p>
        </div>
        {!printMode && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={handlePrint}
              iconName="Printer"
              iconPosition="left"
              title={
                rollups.enriched.length === 0
                  ? 'Opens the print view — will show "no students in this scope".'
                  : 'Open the print-ready view for this report'
              }
            >
              Print / Save as PDF
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCsv}
              iconName="Download"
              iconPosition="left"
              disabled={rollups.enriched.length === 0}
            >
              Export CSV
            </Button>
          </div>
        )}
      </div>

      {rollups.totalStudents === 0 ? (
        <EmptyState
          icon="Users"
          title="No students in scope"
          body="Add a student, or widen the student filter, and their IEP compliance status will show up here."
        />
      ) : (
        <>
          <ChartCard title="Students by IEP review status">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={rollups.chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#6B7280" fontSize={12} />
                <YAxis
                  stroke="#6B7280"
                  fontSize={12}
                  allowDecimals={false}
                />
                {!printMode && (
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                    }}
                  />
                )}
                <Bar
                  dataKey="value"
                  radius={[4, 4, 0, 0]}
                  isAnimationActive={animate}
                  label={printMode ? { position: 'top', fontSize: 11, fill: '#111827' } : undefined}
                >
                  {rollups.chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {rollups.overdueRows.length > 0 && (
            <ComplianceTable
              title="Overdue reviews"
              rows={rollups.overdueRows}
              todayIso={rollups.todayIso}
              onOpen={openStudent}
              accent="danger"
              emptyHint=""
            />
          )}

          <ComplianceTable
            title="Upcoming reviews"
            rows={rollups.upcomingRows}
            todayIso={rollups.todayIso}
            onOpen={openStudent}
            emptyHint="Nothing scheduled yet in the future."
          />

          {rollups.missingDateRows.length > 0 && (
            <div className="bg-surface border border-border rounded-lg overflow-hidden">
              <div className="p-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon
                    name="HelpCircle"
                    size={18}
                    className="text-amber-600"
                  />
                  <h3 className="font-medium text-foreground">
                    Students without an IEP review date
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  These students are in the roster but haven't had their
                  IEP annual-review date entered. Fill it in from the profile.
                </p>
              </div>
              <ul className="divide-y divide-border">
                {rollups.missingDateRows.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => openStudent(r.id)}
                      className="w-full text-left flex items-center gap-3 p-3 hover:bg-muted/50"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {r.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {r.grade ? `Grade ${r.grade} · ` : ''}
                          {r.goalCount} active goal
                          {r.goalCount === 1 ? '' : 's'}
                        </p>
                      </div>
                      <Icon
                        name="ChevronRight"
                        size={16}
                        className="text-muted-foreground"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ComplianceTable = ({ title, rows, onOpen, accent, emptyHint }) => (
  <div className="bg-surface border border-border rounded-lg overflow-hidden">
    <div
      className={`p-4 border-b border-border ${
        accent === 'danger' ? 'bg-red-50/60' : ''
      }`}
    >
      <h3 className="font-medium text-foreground">{title}</h3>
    </div>
    {rows.length === 0 ? (
      <p className="p-6 text-center text-sm text-muted-foreground">
        {emptyHint}
      </p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="p-3">Student</th>
              <th className="p-3">Grade</th>
              <th className="p-3">Review due</th>
              <th className="p-3">Days</th>
              <th className="p-3">Goals</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-muted/30">
                <td className="p-3 font-medium text-foreground">{r.name}</td>
                <td className="p-3 text-foreground">
                  {r.grade || '—'}
                </td>
                <td className="p-3 text-foreground">
                  {r.reviewDue
                    ? new Date(
                        `${String(r.reviewDue).slice(0, 10)}T12:00:00`,
                      ).toLocaleDateString()
                    : '—'}
                </td>
                <td className="p-3">
                  {r.daysUntil === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : r.daysUntil < 0 ? (
                    <span className="font-medium text-red-700">
                      {Math.abs(r.daysUntil)} overdue
                    </span>
                  ) : r.daysUntil <= 30 ? (
                    <span className="font-medium text-amber-700">
                      {r.daysUntil}d
                    </span>
                  ) : (
                    <span className="text-foreground">{r.daysUntil}d</span>
                  )}
                </td>
                <td className="p-3 text-foreground">{r.goalCount}</td>
                <td className="p-3 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onOpen(r.id)}
                    iconName="ChevronRight"
                    iconPosition="right"
                  >
                    Open
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default ComplianceReport;
